import * as TabsPrimitive from '@radix-ui/react-tabs'
import { useScrollFade } from '@shared/lib'
import { cn } from '@shared/lib/cn'
import { TabsOverflow, TabsVariant } from './types'

const LIST_CLASS_PILL = 'flex items-center gap-1 rounded-lg bg-gray-100 p-1 w-fit'
const LIST_CLASS_UNDERLINE = 'border-border mb-6 flex border-b text-sm font-medium'
const LIST_CLASS_BUTTON = 'flex gap-2'
// Переполнение. wrap: вкладки уходят на вторую строку, а не прячутся за краем.
// scroll: одна строка, триггеры не сжимаются (shrink-0 ниже), полоса прокручивается, а у края,
// за которым есть скрытые вкладки, полоса гаснет маской — иначе скрытая вкладка не видна вовсе.
const LIST_CLASS_WRAP = 'flex-wrap'
const LIST_CLASS_SCROLL = 'overflow-x-auto whitespace-nowrap'
const TRIGGER_CLASS_SCROLL = 'shrink-0'
const ACTIVE_TRIGGER_SELECTOR = '[data-state="active"]'
export interface TabItem {
  value: string
  label: React.ReactNode
  count?: number
  className?: string
}

export interface TabsProps {
  items: TabItem[]
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  children?: React.ReactNode
  className?: string
  listClassName?: string
  variant?: TabsVariant
  /** Без пропа вкладки ведут себя как раньше — решает `listClassName`. */
  overflow?: TabsOverflow
  render?: (item: TabItem) => React.ReactNode
}

export function Tabs({
  items,
  defaultValue,
  value,
  onValueChange,
  children,
  className,
  listClassName,
  variant = 'underline',
  overflow,
  render,
}: TabsProps) {
  const isPill = variant === TabsVariant.pill
  const isButton = variant === TabsVariant.button
  const isScroll = overflow === TabsOverflow.scroll
  // Активная вкладка (диплинк, смена value, смена языка) должна быть в видимой части полосы.
  const { ref, onScroll, style } = useScrollFade<HTMLDivElement>({
    enabled: isScroll,
    revealSelector: ACTIVE_TRIGGER_SELECTOR,
    revealKey: value,
    childrenKey: items.length,
  })

  const list = (
    <TabsPrimitive.List
      ref={ref}
      onScroll={onScroll}
      style={style}
      className={cn(
        isPill ? LIST_CLASS_PILL : isButton ? LIST_CLASS_BUTTON : LIST_CLASS_UNDERLINE,
        overflow === TabsOverflow.wrap && LIST_CLASS_WRAP,
        isScroll && LIST_CLASS_SCROLL,
        listClassName
      )}
    >
      {items.map((item) => (
        <TabsPrimitive.Trigger
          key={item.value}
          value={item.value}
          className={cn(
            isScroll && TRIGGER_CLASS_SCROLL,
            isPill
              ? cn(
                  'cursor-pointer rounded-md px-3 py-1 text-xs font-medium transition-all',
                  'text-gray-500 hover:text-gray-900',
                  'data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm'
                )
              : isButton
                ? cn(
                    'inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 text-lg text-sm transition',
                    // 'focus-visible:ring-orange focus-visible:ring-2 focus-visible:outline-none',
                    // 'disabled:cursor-not-allowed disabled:opacity-50',
                    'hover:bg-orange-subtle border-gray-300 bg-transparent',
                    'data-[state=active]:border-orange data-[state=active]:text-orange data-[state=active]:bg-orange-subtle'
                  )
                : cn(
                    'typo-base relative flex cursor-pointer items-center gap-1.5 px-4 py-2.5',
                    'text-secondary border-b-2 border-transparent transition-colors',
                    'hover:text-foreground',
                    'data-[state=active]:border-orange data-[state=active]:text-orange'
                  ),
            // Per-item override wins over base inactive classes via tailwind-merge;
            // the data-[state=active]: orange still wins by CSS specificity.
            item.className
          )}
        >
          {render ? (
            render(item)
          ) : (
            <>
              {item.label}
              {item.count !== undefined && item.count > 0 && (
                <span className="bg-orange-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white">
                  {item.count}
                </span>
              )}
            </>
          )}
        </TabsPrimitive.Trigger>
      ))}
    </TabsPrimitive.List>
  )
  return (
    <TabsPrimitive.Root
      defaultValue={defaultValue ?? items[0]?.value}
      value={value}
      onValueChange={onValueChange}
      className={className}
    >
      {list}

      {children}
    </TabsPrimitive.Root>
  )
}
